import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { AUTH_TOKEN } from 'constants/AuthConstant'
import AuthService from 'services/AuthService'

const initialState = {
	loading: false,
	token: localStorage.getItem(AUTH_TOKEN) || null,
	message: '',
	showMessage: false
}

// =======================
// THUNKS
// =======================

export const signIn = createAsyncThunk(
	'auth/signIn',
	async ({ email, password }, { rejectWithValue }) => {
		try {
			const response = await AuthService.login({ email, password })
			const token = response.data.token
			localStorage.setItem(AUTH_TOKEN, token)
			return token
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Login failed')
		}
	}
)

export const signUp = createAsyncThunk(
	'auth/signUp',
	async ({ email, password }, { rejectWithValue }) => {
		try {
			const response = await AuthService.register({ email, password })
			const token = response.data.token
			localStorage.setItem(AUTH_TOKEN, token)
			return token
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Signup failed')
		}
	}
)

export const signOut = createAsyncThunk('auth/signOut', async () => {
	localStorage.removeItem(AUTH_TOKEN)
	return true
})

export const signInWithGoogle = createAsyncThunk(
	'auth/signInWithGoogle',
	async (_, { rejectWithValue }) => {
		try {
			const response = await AuthService.loginInOAuth()
			const token = response.data.token
			localStorage.setItem(AUTH_TOKEN, token)
			return token
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Google login failed')
		}
	}
)

export const signInWithFacebook = createAsyncThunk(
	'auth/signInWithFacebook',
	async (_, { rejectWithValue }) => {
		try {
			const response = await AuthService.loginInOAuth()
			const token = response.data.token
			localStorage.setItem(AUTH_TOKEN, token)
			return token
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Facebook login failed')
		}
	}
)

// =======================
// SLICE
// =======================

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		// ✅ compatibility reducer (used in LoginForm)
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},

		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},

		// ✅ compatibility reducer (used in LoginForm)
		showLoading: (state) => {
			state.loading = true
		},

		// ✅ compatibility reducer (used in FetchInterceptor)
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.message = ''
			state.showMessage = false
		}
	},
	extraReducers: (builder) => {
		builder
			// SIGN IN
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false
				state.token = action.payload
			})
			.addCase(signIn.rejected, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})

			// SIGN UP
			.addCase(signUp.pending, (state) => {
				state.loading = true
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false
				state.token = action.payload
			})
			.addCase(signUp.rejected, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})

			// GOOGLE
			.addCase(signInWithGoogle.fulfilled, (state, action) => {
				state.loading = false
				state.token = action.payload
			})
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})

			// FACEBOOK
			.addCase(signInWithFacebook.fulfilled, (state, action) => {
				state.loading = false
				state.token = action.payload
			})
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})

			// SIGN OUT
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.message = ''
				state.showMessage = false
			})
	}
})

// ✅ EXPORTS (THIS FIXES YOUR BUILD)
export const {
	showAuthMessage,
	hideAuthMessage,
	showLoading,
	signOutSuccess
} = authSlice.actions

export default authSlice.reducer
